/**
 * Unified installments context provider - React state management
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { UnifiedInstallment, InstallmentRelationship } from '@/types/unified-installments';
import { useProfile } from '@/contexts/profile';
import type { UnifiedInstallmentsContextType } from './types';
import {
  loadInstallmentsFromStorage,
  loadRelationshipsFromStorage,
  saveInstallmentsToStorage,
  saveRelationshipsToStorage,
  createInstallment,
  filterInstallmentsByParent,
  findInstallmentByTransaction,
  findTransactionByInstallment,
  createRelationship,
  getPaidUpdates,
  getPendingUpdates
} from './operations';

const UnifiedInstallmentsContext = createContext<UnifiedInstallmentsContextType | undefined>(undefined);

export const UnifiedInstallmentsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [installments, setInstallments] = useState<UnifiedInstallment[]>([]);
  const [relationships, setRelationships] = useState<InstallmentRelationship[]>([]);
  
  // Safely get profile context - it might not be available during initial render
  let activeProfile = null;
  try {
    const profileContext = useProfile();
    activeProfile = profileContext.activeProfile;
  } catch (error) {
    // ProfileProvider not available yet - this is OK during app initialization
    console.log('[UnifiedInstallments] ProfileProvider not available yet');
  }

  // Load data when profile changes
  useEffect(() => {
    if (!activeProfile) return;

    setInstallments(loadInstallmentsFromStorage(activeProfile.id));
    setRelationships(loadRelationshipsFromStorage(activeProfile.id));
  }, [activeProfile?.id]);

  // Save data when it changes
  useEffect(() => {
    if (!activeProfile) return;

    saveInstallmentsToStorage(activeProfile.id, installments);
    saveRelationshipsToStorage(activeProfile.id, relationships);
  }, [installments, relationships, activeProfile?.id]);

  // Listen for transaction status changes to sync installments bidirectionally
  useEffect(() => {
    const handleTransactionStatusChanged = (event: Event) => {
      const customEvent = event as CustomEvent<{ 
        transactionId: string; 
        newStatus: string; 
        paidDate: string;
      }>;
      const { transactionId, newStatus, paidDate } = customEvent.detail;
      
      // Find installment linked to this transaction
      const installment = findInstallmentByTransaction(installments, relationships, transactionId);
      
      if (installment) {
        // Only sync if the status is actually different
        const installmentIsPaid = installment.status === 'paid';
        const transactionIsPaid = newStatus === 'paid';
        
        if (installmentIsPaid !== transactionIsPaid) {
          console.log('📦 Syncing installment with transaction status change:', { 
            installmentId: installment.id, 
            transactionId,
            newStatus 
          });
          
          if (transactionIsPaid) {
            // Mark installment as paid
            setInstallments(prev =>
              prev.map(i =>
                i.id === installment.id 
                  ? { ...i, ...getPaidUpdates(transactionId, paidDate) } 
                  : i
              )
            );
          } else {
            // Mark installment as pending
            setInstallments(prev =>
              prev.map(i =>
                i.id === installment.id 
                  ? { ...i, ...getPendingUpdates() } 
                  : i
              )
            );
          }
        }
      }
    };

    window.addEventListener('transaction-status-changed', handleTransactionStatusChanged);
    return () => window.removeEventListener('transaction-status-changed', handleTransactionStatusChanged);
  }, [installments, relationships]);

  const addInstallment = (installmentData: Omit<UnifiedInstallment, 'id'>): string => {
    const newInstallment = createInstallment(installmentData);
    setInstallments(prev => [...prev, newInstallment]);
    console.log('✅ Nova parcela unificada criada:', newInstallment);
    return newInstallment.id;
  };

  const updateInstallment = (id: string, updates: Partial<UnifiedInstallment>) => {
    setInstallments(prev =>
      prev.map(installment =>
        installment.id === id ? { ...installment, ...updates } : installment
      )
    );
    console.log('✅ Parcela unificada atualizada:', id, updates);
  };

  const deleteInstallment = (id: string) => {
    setInstallments(prev => prev.filter(installment => installment.id !== id));
    setRelationships(prev => prev.filter(rel => rel.installmentId !== id));
    console.log('🗑️ Parcela unificada removida:', id);
  };

  const getInstallmentsByParent = (parentId: string, parentType: 'debt' | 'installmentGroup'): UnifiedInstallment[] => {
    return filterInstallmentsByParent(installments, parentId, parentType);
  };

  const linkTransactionToInstallment = (installmentId: string, transactionId: string) => {
    // Remove any existing relationship for this installment
    setRelationships(prev => prev.filter(rel => rel.installmentId !== installmentId));
    
    // Add new relationship
    const newRelationship = createRelationship(installmentId, transactionId);
    setRelationships(prev => [...prev, newRelationship]);
    console.log('🔗 Transação vinculada à parcela:', { installmentId, transactionId });
  };

  const unlinkTransactionFromInstallment = (installmentId: string) => {
    setRelationships(prev => prev.filter(rel => rel.installmentId !== installmentId));
    console.log('🔗 Transação desvinculada da parcela:', installmentId);
  };

  const getInstallmentByTransaction = (transactionId: string): UnifiedInstallment | undefined => {
    return findInstallmentByTransaction(installments, relationships, transactionId);
  };

  const getTransactionByInstallment = (installmentId: string): string | undefined => {
    return findTransactionByInstallment(relationships, installmentId);
  };

  const markInstallmentAsPaid = (installmentId: string, transactionId: string, paidDate: string) => {
    updateInstallment(installmentId, getPaidUpdates(transactionId, paidDate));
    linkTransactionToInstallment(installmentId, transactionId);
    console.log('💰 Parcela marcada como paga:', { installmentId, transactionId, paidDate });
  };

  const markInstallmentAsPending = (installmentId: string) => {
    const transactionId = getTransactionByInstallment(installmentId);
    updateInstallment(installmentId, getPendingUpdates());
    unlinkTransactionFromInstallment(installmentId);
    console.log('⏳ Parcela marcada como pendente:', installmentId);
    return transactionId;
  };

  return (
    <UnifiedInstallmentsContext.Provider
      value={{
        installments,
        relationships,
        addInstallment,
        updateInstallment,
        deleteInstallment,
        getInstallmentsByParent,
        linkTransactionToInstallment,
        unlinkTransactionFromInstallment,
        getInstallmentByTransaction,
        getTransactionByInstallment,
        markInstallmentAsPaid,
        markInstallmentAsPending
      }}
    >
      {children}
    </UnifiedInstallmentsContext.Provider>
  );
};

export const useUnifiedInstallments = () => {
  const context = useContext(UnifiedInstallmentsContext);
  if (context === undefined) {
    throw new Error('useUnifiedInstallments must be used within a UnifiedInstallmentsProvider');
  }
  return context;
};

export { UnifiedInstallmentsContext };
